import axios from './axiosConfig';
import { API_URL } from '../config';
import { UserRole } from '../types/enums';

export interface Campaign {
  id: number;
  name: string;
  dashboard_url: string;
  platform_id: string;
  matomo_id: string;
  primary_color: string;
  secondary_color: string;
  chatbot_enabled: boolean;
}

export interface UserCreateData {
  username: string;
  full_name: string;
  email: string;
  password: string;
  phone?: string;
  crm_channels?: string[];
  role: UserRole;
  campaign_ids?: number[];
}

export interface User {
  id: number;
  username: string;
  full_name?: string;
  email: string;
  phone?: string;
  crm_channels?: string[];
  role: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
  campaigns?: Campaign[];
}

export interface ChatResponse {
  answer: string;
  token_usage?: number;
}

export const UserService = {

  getAllUsers: async (): Promise<User[]> => {
    try {
      const response = await axios.get(`${API_URL}/auth/users`);
      return response.data.items;
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  },

  getUserById: async (id: number): Promise<User> => {
    try {
      const response = await axios.get(`${API_URL}/auth/users/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching user by ID (${id}):`, error);
      throw error;
    }
  },

  createUser: async (userData: UserCreateData): Promise<User> => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, userData);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  updateUser: async (id: number, userData: Partial<User>): Promise<User> => {
    try {
      const response = await axios.put(`${API_URL}/auth/users/${id}`, userData);
      return response.data;
    } catch (error) {
      console.error(`Error updating user (${id}):`, error);
      throw error;
    }
  },

  deleteUser: async (id: number): Promise<void> => {
    try {
      await axios.delete(`${API_URL}/auth/users/${id}`);
    } catch (error) {
      console.error(`Error deleting user (${id}):`, error);
      throw error;
    }
  },

  setUserActive: async (id: number, isActive: boolean): Promise<User> => {
    try {
      const response = await axios.put(`${API_URL}/auth/users/${id}`, { is_active: isActive });
      return response.data;
    } catch (error) {
      console.error(`Error updating status for user (${id}):`, error);
      throw error;
    }
  },

  createClientUser: async (payload: {
    username: string;
    full_name: string;
    email: string;
    password: string;
    phone?: string;
    campaign_ids?: number[];
  }) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, {
        ...payload,
        role: UserRole.USER_CLIENT,
      });
      return response.data as User;
    } catch (error) {
      console.error("Error creating client user:", error);
      throw error;
    }
  },

  getUserWithCamaigns: async (): Promise<User> => {
    try {
      const response = await axios.get(`${API_URL}/auth/user/me`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user with campaigns:', error);
      throw error;
    }
  },

  updateMe: async (payload: Partial<User>): Promise<User> => {
    try {
      const response = await axios.put(`${API_URL}/auth/user/me`, payload);
      return response.data;
    } catch (error) {
      console.error("Error updating current user:", error);
      throw error;
    }
  },

  changeMyPassword: async (payload: { current_password: string; new_password: string }): Promise<void> => {
    try {
      await axios.put(`${API_URL}/auth/user/me/password`, payload);
    } catch (error) {
      console.error("Error changing password:", error);
      throw error;
    }
  },

};
